import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { get, post, toastError } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Toolbar, SearchInput } from "@/components/common/Page";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState, LoadingState } from "@/components/common/States";
import { AddressInput } from "@/components/common/AddressInput";
import { nok, fmtDate, relDays } from "@/lib/format";
import { LEAD_STAGES, NEED_TYPES, label } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Inbox, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const OPEN_STAGES = LEAD_STAGES.filter((s) => !["vunnet", "tapt"].includes(s));

export default function LeadsPage() {
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const { filters, setFilter, setMany, reset, active } = useFilters("leads", { view: "board", stage: "all", owner: "all", q: "", ny: "" });
  const [sp] = useSearchParams();
  // «Lag tilbud» from a capacity plan pre-fills the need; the quote itself follows the normal lead → kalkyle → tilbud flow.
  const prefill = sp.get("plan") ? { customer_id: sp.get("kunde") || "", persons: sp.get("persons") || "", desired_start: sp.get("start") || "", duration_months: sp.get("months") || "", location_address: sp.get("sted") || "", title: sp.get("sted") ? `Innkvartering ${sp.get("persons") || ""} personer – ${sp.get("sted")}` : "", description: `Opprettet fra kapasitetsplan (Planlegging → Kapasitet: /planlegging/kapasitet?plan=${sp.get("plan")}). Behov: ${sp.get("persons") || "?"} personer fra ${sp.get("start") || "?"} i ${sp.get("months") || "?"} måneder.`, source: "kapasitet" } : null;
  const [createOpen, setCreateOpen] = useState(!!filters.ny);
  useEffect(() => {
    if (filters.ny) setCreateOpen(true);
  }, [filters.ny]);

  const { data, isLoading, error, refetch } = useQuery({ queryKey: ["leads"], queryFn: () => get("/leads") });
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: () => get("/users"), enabled: can("users:read") });

  const rows = (data || []).filter((l) => (filters.stage === "all" || l.stage === filters.stage) && (filters.owner === "all" || l.owner_id === filters.owner) && (!filters.q || `${l.title} ${l.customer_name} ${l.lead_number} ${l.location_address}`.toLowerCase().includes(filters.q.toLowerCase())));

  const columns = [
    { key: "lead_number", header: "Nr", render: (r) => <span className="font-mono text-[12px] text-muted-foreground">{r.lead_number}</span> },
    { key: "title", header: "Henvendelse", render: (r) => <div><div className="font-medium">{r.title}</div><div className="text-xs text-muted-foreground">{r.customer_name} · {r.contact_name || "–"}</div></div> },
    { key: "stage", header: "Fase", render: (r) => <StatusBadge status={r.stage} entity="lead" /> },
    { key: "need_type", header: "Behov", render: (r) => <span>{label(r.need_type)}{r.persons ? ` · ${r.persons} pers` : ""}</span> },
    { key: "location_address", header: "Lokasjon", render: (r) => <span className="text-muted-foreground">{r.location_address || "–"}</span> },
    { key: "desired_start", header: "Ønsket start", render: (r) => <span className="tabular">{fmtDate(r.desired_start)}</span> },
    { key: "estimated_value", header: "Est. verdi", align: "right", render: (r) => nok(r.estimated_value) },
    { key: "owner_name", header: "Ansvarlig", render: (r) => r.owner_name || <span className="text-muted-foreground">Ikke tildelt</span> },
    { key: "source", header: "Kilde", render: (r) => <span className="text-xs text-muted-foreground">{label(r.source)}</span> },
  ];

  return (
    <div className="page-pad space-y-4 fade-up" data-testid="leads-page">
      <PageHeader
        title="Henvendelser"
        count={(data || []).filter((l) => !['vunnet', 'tapt'].includes(l.stage)).length}
        subtitle={`Pipeline ${nok((data || []).filter((l) => !['vunnet', 'tapt'].includes(l.stage)).reduce((s, l) => s + (l.estimated_value || 0), 0))} · åpne henvendelser`}
        actions={can("leads:write") && <Button onClick={() => setCreateOpen(true)} data-testid="leads-create-button"><Plus /> Ny henvendelse</Button>}
      />
      <Toolbar>
        <Tabs value={filters.view} onValueChange={(v) => setFilter("view", v)}>
          <TabsList className="h-8">
            <TabsTrigger value="board" data-testid="leads-view-board">Pipeline</TabsTrigger>
            <TabsTrigger value="table" data-testid="leads-view-table">Tabell</TabsTrigger>
          </TabsList>
        </Tabs>
        <span className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden />
        <SearchInput value={filters.q} onChange={(v) => setFilter("q", v)} className="w-48" testId="leads-search-input" />
        {filters.view === "table" && (
          <Select value={filters.stage} onValueChange={(v) => setFilter("stage", v)}>
            <SelectTrigger className="h-8 w-44" data-testid="leads-stage-filter"><SelectValue placeholder="Fase" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Alle faser</SelectItem>{LEAD_STAGES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {users && (
          <Select value={filters.owner} onValueChange={(v) => setFilter("owner", v)}>
            <SelectTrigger className="h-8 w-44" data-testid="leads-owner-filter"><SelectValue placeholder="Ansvarlig" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Alle selgere</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {active > 0 && <Button variant="ghost" onClick={reset} data-testid="filters-reset-button"><X className="h-3.5 w-3.5" /> Nullstill</Button>}
      </Toolbar>

      {filters.view === "board" ? (
        isLoading ? <LoadingState /> : <Board leads={rows} onOpen={(l) => navigate(`/henvendelser/${l.id}`)} />
      ) : (
        <DataTable columns={columns} rows={rows} isLoading={isLoading} error={error} onRetry={refetch} onRowClick={(r) => navigate(`/henvendelser/${r.id}`)} testId="leads-table" rowTestId={(r) => `lead-row-${r.id}`} empty={<EmptyState title="Ingen henvendelser" description="Opprett en henvendelse manuelt eller vent på innsendinger fra nettsiden." icon={Inbox} />} />
      )}

      <LeadCreateSheet open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setMany({ ny: "", plan: "", persons: "", start: "", months: "", sted: "", kunde: "" }); }} users={users} defaultOwner={user?.id} prefill={prefill} />
    </div>
  );
}

const Board = ({ leads, onOpen }) => {
  const qc = useQueryClient();
  const move = useMutation({
    mutationFn: ({ id, stage }) => post(`/leads/${id}/stage`, { stage }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
    onError: (e) => toastError(e),
  });
  const [dragId, setDragId] = useState(null);
  return (
    <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-thin -mx-1 px-1" data-testid="leads-kanban">
      {OPEN_STAGES.map((stage) => {
        const items = leads.filter((l) => l.stage === stage);
        const value = items.reduce((s, l) => s + (l.estimated_value || 0), 0);
        return (
          <div
            key={stage}
            className={cn("flex max-h-[calc(100vh-232px)] w-[264px] shrink-0 flex-col rounded-md border bg-muted/30", dragId && "border-primary/40")}
            data-testid={`lead-kanban-column-${stage}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              if (dragId) move.mutate({ id: dragId, stage });
              setDragId(null);
            }}
          >
            <div className="flex h-9 items-center justify-between border-b px-3">
              <StatusBadge status={stage} entity="lead" className="font-medium" />
              <div className="text-[11.5px] text-muted-foreground tabular">{items.length} · {value ? nok(value / 1000, { bare: true }) + "k" : "–"}</div>
            </div>
            <div className="space-y-1.5 overflow-y-auto p-1.5 scrollbar-thin">
              {items.length === 0 && <div className="py-6 text-center text-[12px] text-muted-foreground">Ingen</div>}
              {items.map((l) => (
                <button
                  key={l.id}
                  draggable
                  onDragStart={() => setDragId(l.id)}
                  onDragEnd={() => setDragId(null)}
                  onClick={() => onOpen(l)}
                  data-testid={`lead-kanban-card-${l.id}`}
                  className="block w-full rounded-md border bg-card px-3 py-2.5 text-left transition-colors duration-150 hover:border-foreground/30 focus-visible:border-foreground/40 focus-visible:outline-none"
                >
                  <div className="line-clamp-2 text-[13px] font-medium leading-snug">{l.title}</div>
                  <div className="mt-0.5 truncate text-[12px] text-muted-foreground">{l.customer_name}</div>
                  <div className="mt-2 flex items-center justify-between text-[12px]">
                    <span className="tabular">{l.estimated_value ? nok(l.estimated_value) : "–"}</span>
                    <span className="text-muted-foreground">{l.desired_start ? relDays(l.desired_start) : ""}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                    <span className="font-mono">{l.lead_number}</span> · {label(l.need_type)}{l.persons ? ` ${l.persons}p` : ""} · {l.owner_name?.split(" ")[0] || "–"}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const LeadCreateSheet = ({ open, onOpenChange, users, defaultOwner, customerId, customerName, prefill }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const empty = { customer_id: customerId || "", customer_name: customerName || "", contact_name: "", contact_email: "", contact_phone: "", title: "", need_type: "innkvartering", persons: "", location_address: "", location_place_id: "", desired_start: "", duration_months: "", description: "", owner_id: defaultOwner || "", estimated_value: "", source: "manuell" };
  const [f, setF] = useState(empty);
  const { data: customers } = useQuery({ queryKey: ["customers"], queryFn: () => get("/customers"), enabled: open && !customerId });
  useEffect(() => { if (open) setF({ ...empty, owner_id: defaultOwner || "", ...(prefill || {}) }); }, [open]); // reset the form each time the sheet opens
  const create = useMutation({
    mutationFn: () => post("/leads", { ...f, customer_id: f.customer_id || null, customer_name: f.customer_id ? null : f.customer_name, persons: f.persons ? Number(f.persons) : null, duration_months: f.duration_months ? Number(f.duration_months) : null, estimated_value: f.estimated_value ? Number(f.estimated_value) : null, desired_start: f.desired_start || null, owner_id: f.owner_id || null }),
    onSuccess: (l) => {
      toast.success(`Henvendelse ${l.lead_number} opprettet`);
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      onOpenChange(false);
      navigate(`/henvendelser/${l.id}`);
    },
    onError: (e) => toastError(e),
  });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[520px] max-w-[94vw] overflow-y-auto" data-testid="lead-create-sheet">
        <SheetHeader><SheetTitle>Ny henvendelse</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-4">
          {!customerId && (
            <div>
              <Label>Kunde *</Label>
              <Select value={f.customer_id || "__new"} onValueChange={(v) => setF({ ...f, customer_id: v === "__new" ? "" : v })}>
                <SelectTrigger data-testid="lead-customer-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__new">+ Ny kunde (skriv navn under)</SelectItem>{(customers || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              {!f.customer_id && <Input className="mt-2" placeholder="Firmanavn" value={f.customer_name} onChange={set("customer_name")} data-testid="lead-customer-name-input" />}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Kontaktperson</Label><Input value={f.contact_name} onChange={set("contact_name")} data-testid="lead-contact-name-input" /></div>
            <div><Label>Telefon</Label><Input value={f.contact_phone} onChange={set("contact_phone")} /></div>
            <div className="col-span-2"><Label>E-post</Label><Input type="email" value={f.contact_email} onChange={set("contact_email")} /></div>
          </div>
          <div><Label>Tittel</Label><Input value={f.title} onChange={set("title")} placeholder="Standard: kunde + behov" data-testid="lead-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type behov *</Label>
              <Select value={f.need_type} onValueChange={set("need_type")}>
                <SelectTrigger data-testid="lead-need-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{NEED_TYPES.map((n) => <SelectItem key={n} value={n}>{label(n)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Antall personer</Label><Input type="number" value={f.persons} onChange={set("persons")} data-testid="lead-persons-input" /></div>
          </div>
          <div>
            <Label>Prosjektadresse</Label>
            <AddressInput value={f.location_address} onChange={(v) => setF((p) => ({ ...p, location_address: v }))} onSelect={(s) => setF((p) => ({ ...p, location_address: s.address, location_place_id: s.place_id }))} testId="lead-address-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Ønsket klar</Label><Input type="date" value={f.desired_start} onChange={set("desired_start")} data-testid="lead-start-input" /></div>
            <div><Label>Varighet (mnd)</Label><Input type="number" value={f.duration_months} onChange={set("duration_months")} data-testid="lead-duration-input" /></div>
            <div><Label>Est. verdi (kr)</Label><Input type="number" value={f.estimated_value} onChange={set("estimated_value")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Kilde</Label>
              <Select value={f.source} onValueChange={set("source")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["manuell", "nettside", "telefon", "e-post", "referanse", "messe", "eksisterende kunde", "kapasitet"].map((s) => <SelectItem key={s} value={s}>{label(s) || s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {users && (
              <div>
                <Label>Ansvarlig selger</Label>
                <Select value={f.owner_id || "none"} onValueChange={(v) => setF({ ...f, owner_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Ikke tildelt</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div><Label>Beskrivelse</Label><Textarea rows={3} value={f.description} onChange={set("description")} data-testid="lead-description-input" /></div>
          <Button className="w-full" disabled={create.isPending || (!f.customer_id && !f.customer_name)} onClick={() => create.mutate()} data-testid="lead-create-submit-button">
            {create.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Opprett henvendelse
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
